import {
	getTraceLotById,
	getTraceGraph,
	getTraceSummary,
	getIncomeLotForLot,
	getAllOutcomeLotsForLot,
	getAllProductionStepResultLotsForLot,
	getAllProductionStepResourceLotsForLot,
	getAllResourcesForProductionStep,
	getAllResultsForProductionStep,
	getProductionOrderForProductionStep,
	TraceGraph,
	TraceLot,
	IncomeLot,
	OutcomeLot,
	ProductionStepResultLot,
	ProductionStepResourceLot,
	ProductionOrder,
} from "@q-food/q-food";

/**
 * Trace cache for the directional trace modal (summary + recursive tree). The modal fetches the whole
 * trace graph in one request and seeds every per-lot / per-step accessor from it via `primeTraceGraph`,
 * so the summary and tree render from cache in a single pass instead of walking the graph node-by-node.
 *
 * The graph nodes are lean (only the fields the trace view reads). The general lot-trace and the hover
 * tooltip don't use this cache — they call the full endpoints directly — so the lean shape stays here.
 */
type Cached<T> = ((id: string) => Promise<T>) & { prime: (id: string, value: T) => void };

const cacheClearers: Array<() => void> = [];

function memoize<T>(fn: (id: string) => Promise<T>): Cached<T> {
	const cache = new Map<string, Promise<T>>();
	cacheClearers.push(() => cache.clear());

	const wrapped = ((id: string) => {
		let cached = cache.get(id);
		if (!cached) {
			cached = fn(id).catch((error) => {
				cache.delete(id);
				throw error;
			});
			cache.set(id, cached);
		}
		return cached;
	}) as Cached<T>;

	wrapped.prime = (id, value) => {
		cache.set(id, Promise.resolve(value));
	};

	return wrapped;
}

export const cachedGetTraceLotById = memoize(getTraceLotById);
export const cachedGetTraceSummary = memoize(getTraceSummary);
export const cachedGetIncomeLotForLot = memoize(getIncomeLotForLot);
export const cachedGetAllOutcomeLotsForLot = memoize(getAllOutcomeLotsForLot);
export const cachedGetAllProductionStepResultLotsForLot = memoize(getAllProductionStepResultLotsForLot);
export const cachedGetAllProductionStepResourceLotsForLot = memoize(getAllProductionStepResourceLotsForLot);
export const cachedGetAllResourcesForProductionStep = memoize(getAllResourcesForProductionStep);
export const cachedGetAllResultsForProductionStep = memoize(getAllResultsForProductionStep);
export const cachedGetProductionOrderForProductionStep = memoize(getProductionOrderForProductionStep);

const traceGraphCache = new Map<string, Promise<TraceGraph>>();
cacheClearers.push(() => traceGraphCache.clear());

export function cachedGetTraceGraph(id: string, direction: "upstream" | "downstream"): Promise<TraceGraph> {
	const key = `${id}:${direction}`;
	let cached = traceGraphCache.get(key);
	if (!cached) {
		cached = getTraceGraph(id, direction).catch((error) => {
			traceGraphCache.delete(key);
			throw error;
		});
		traceGraphCache.set(key, cached);
	}
	return cached;
}

/**
 * Seed every accessor from a single graph response so the summary and the recursive tree render without
 * any further requests. The graph node is a lean shape whose field names match the subset the trace
 * components read, so it is cast to the full generated types the accessors are typed to return.
 */
export function primeTraceGraph(graph: TraceGraph) {
	const resultsByStep: Record<string, unknown[]> = {};
	const resourcesByStep: Record<string, unknown[]> = {};

	for (const lot of graph.lots) {
		cachedGetTraceLotById.prime(lot.id, lot as unknown as TraceLot);
		cachedGetIncomeLotForLot.prime(lot.id, lot.incomeLot as unknown as IncomeLot);
		cachedGetAllOutcomeLotsForLot.prime(lot.id, lot.outcomeLots as unknown as OutcomeLot[]);
		cachedGetAllProductionStepResultLotsForLot.prime(
			lot.id,
			lot.productionStepResultLot as unknown as ProductionStepResultLot,
		);
		cachedGetAllProductionStepResourceLotsForLot.prime(
			lot.id,
			lot.productionStepResourceLot as unknown as ProductionStepResourceLot[],
		);

		if (lot.productionStepResultLot) {
			(resultsByStep[lot.productionStepResultLot.productionStep.id] ??= []).push(lot.productionStepResultLot);
		}
		for (const resource of lot.productionStepResourceLot) {
			(resourcesByStep[resource.productionStep.id] ??= []).push(resource);
		}
	}

	for (const [stepId, results] of Object.entries(resultsByStep)) {
		cachedGetAllResultsForProductionStep.prime(stepId, results as unknown as ProductionStepResultLot[]);
	}
	for (const [stepId, resources] of Object.entries(resourcesByStep)) {
		cachedGetAllResourcesForProductionStep.prime(stepId, resources as unknown as ProductionStepResourceLot[]);
	}

	for (const order of graph.productionOrders) {
		cachedGetProductionOrderForProductionStep.prime(order.productionStepId, order as unknown as ProductionOrder);
	}
}

/**
 * Drop the whole trace cache. Trace graphs span lots, income/outcome lots and production steps/orders,
 * so any of those changing can invalidate an already-cached trace. Clearing everything is simpler and
 * safer than evicting the exact affected entries, and the next trace view refetches lazily.
 */
export function clearTraceCache() {
	cacheClearers.forEach((clear) => clear());
}

// Resources whose mutations can change a trace. When one of their grids refreshes — via
// refreshGridsOfResource() or the websocket refresh-grid push — an already-cached trace is stale, so
// the cache is dropped. This also bounds memory: the cache can't grow unbounded across a long-lived tab
// because ordinary data changes clear it as the user works.
const TRACE_RESOURCES = new Set([
	"lot",
	"income-lot",
	"outcome-lot",
	"production-step",
	"production-order",
	"sales-order",
	"sales-order-row",
	"purchase-order",
	"purchase-order-row",
]);

if (typeof window !== "undefined") {
	window.addEventListener("food:grid:refresh-all", () => clearTraceCache());
	window.addEventListener("food:grid:refresh-resource", (event) => {
		const resource = (event as CustomEvent<{ resource?: string }>).detail?.resource;
		if (resource && TRACE_RESOURCES.has(resource)) clearTraceCache();
	});
}
